import {
  useCallback,
  useMemo,
  useState,
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import { Button, Tooltip } from '@eye-able/ui';
import { InlineTranslationDialog } from './InlineTranslationDialog';
import {
  Eye,
  EyeOff,
  Languages,
  MessageSquareQuote,
  Minimize2,
  Rocket,
} from 'lucide-react';
import { cn } from '@helpers/cn';
import type { TOptions } from 'i18next';

type DialogProps = {
  key?: string;
  options?: TOptions;
};

type InlineTranslationContextType = {
  openDialog: (data: DialogProps) => void;
  editMode: boolean;
};
const InlineTranslationContext = createContext<InlineTranslationContextType>({
  openDialog: () => {},
  editMode: false,
});

export const useInlineTranslationDialog = () =>
  useContext(InlineTranslationContext);

export function InlineTranslationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [editMode, setEditMode] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isToolbarExpanded, setIsToolbarExpanded] = useState(false);
  const [activeTranslation, setActiveTranslation] = useState<DialogProps>();

  const openDialog = useCallback((params: DialogProps) => {
    setIsDialogOpen(true);
    setActiveTranslation(params);
  }, []);

  const closeDialog = useCallback(() => {
    setActiveTranslation(undefined);
    setIsDialogOpen(false);
  }, []);

  const contextValue = useMemo(
    () => ({ editMode, openDialog }),
    [editMode, openDialog],
  );

  const buttonClassName = cn('eye:bg-yellow-100 eye:hover:bg-yellow-hover');

  if (import.meta.env.VITE_INLINE_TRANSLATION !== 'true') {
    return children;
  }

  return (
    <InlineTranslationContext.Provider value={contextValue}>
      {children}
      {isDialogOpen && (
        <InlineTranslationDialog
          translationKey={activeTranslation?.key}
          namespace={activeTranslation?.options?.ns?.toString()}
          onClose={closeDialog}
        />
      )}
      <div className='eye:fixed eye:right-20 eye:bottom-20 eye:z-1000'>
        <div
          className={cn(
            'eye:flex eye:items-center eye:overflow-hidden eye:rounded-full eye:border eye:border-grey-40',
            'eye:bg-white eye:[box-shadow:0_16px_30px_rgba(18,18,18,0.28)] eye:transition-[width] eye:duration-300 eye:ease-out',
            'eye:gap-xs eye:justify-center eye:p-3xs',
            isToolbarExpanded ? 'eye:h-52 eye:w-fit' : 'eye:size-52',
          )}
        >
          <Button
            aria-label='Toggle translation toolbar'
            title='Toggle translation toolbar'
            onClick={() => setIsToolbarExpanded((prev) => !prev)}
            size='icon'
            variant='ghost'
          >
            {isToolbarExpanded ? (
              <Minimize2 aria-hidden className='eye:size-16!' />
            ) : (
              <Languages aria-hidden className='eye:size-16!' />
            )}
          </Button>
          {isToolbarExpanded && (
            <div className='eye:flex eye:items-center eye:gap-xs eye:animate-in eye:fade-in-0 eye:duration-200'>
              <Tooltip content='Show translations dialog' delayDuration={1500}>
                <Button
                  className={buttonClassName}
                  aria-label='Show translations dialog'
                  onClick={() => setIsDialogOpen(true)}
                  size='icon'
                  variant='ghost'
                >
                  <MessageSquareQuote aria-hidden className='eye:size-16!' />
                </Button>
              </Tooltip>
              <Tooltip
                content='Toggle inline translations'
                delayDuration={1500}
              >
                <Button
                  className={buttonClassName}
                  aria-label='Toggle inline translations'
                  aria-pressed={editMode}
                  onClick={() => setEditMode((prev) => !prev)}
                  size='icon'
                  variant='ghost'
                >
                  {editMode ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
                </Button>
              </Tooltip>
              <Tooltip
                content='Release translations'
                delayDuration={1500}
                collisionPadding={16}
              >
                <Button
                  className={buttonClassName}
                  aria-label='Release translations'
                  size='icon'
                  variant='ghost'
                  asChild
                >
                  <a href='/i18n'>
                    <Rocket aria-hidden className='eye:size-16!' />
                  </a>
                </Button>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    </InlineTranslationContext.Provider>
  );
}
